/**
 * Billing group name code that drives the credit sign rule.
 *
 * This value is a backend-managed string (not a TS enum) — keep it as a plain
 * string constant so it stays in sync with the backend vocabulary.
 *
 * Verified against live `/invoice/v1/billings/groups` response 2026-10-05 on
 * test env: the backend returns the camelCase code `creditGroup`.
 */
export const CREDIT_GROUP = 'creditGroup';

/**
 * Normalizes the sign of a billing UNIT PRICE based on the selected billing group.
 *
 * Business rule BR-1 (EB-4521, replicates legacy KV `KundenBillingFrame.addbilling`):
 * the sign of a credit lives on the unit price, NEVER on the quantity. The
 * backend (`invoice-api` `setUpBillingDBO`) silently stores any negative
 * quantity as 0, so a signed quantity produces a 0 € line.
 *
 * Rules:
 *   - `creditGroup` -> the unit price is always negative (credit notes /
 *     Gutschriften). Legacy KV flips a positive price to negative and leaves a
 *     negative one untouched; applying `-Math.abs` yields the same result and
 *     is idempotent.
 *   - any other group code -> unit price returned unchanged (KV does no sign
 *     handling for other groups).
 *
 * Edge cases:
 *   - Zero always returns `0` (never `-0`); a zero credit price is rejected by
 *     the form validator, not by this function.
 *   - Non-finite inputs (`NaN`, `Infinity`, `-Infinity`) are returned
 *     unchanged — validation of such values is the caller's concern.
 *
 * Pure function: no side effects, no logging, no DI.
 *
 * @param groupNameCode The billing group's name code (backend string).
 * @param unitPrice The raw unit price as entered by the user.
 * @returns The unit price with its sign normalized per the rule for the group.
 */
export function normalizeCreditUnitPrice(groupNameCode: string, unitPrice: number): number {
  // Pass non-finite values through untouched — validation layer handles them.
  if (!Number.isFinite(unitPrice)) {
    return unitPrice;
  }

  // Normalize zero to positive zero so callers never see -0.
  if (unitPrice === 0) {
    return 0;
  }

  return groupNameCode === CREDIT_GROUP ? -Math.abs(unitPrice) : unitPrice;
}
